import { lazy, ReactNode, Suspense, useCallback, useEffect, useState } from 'react';

import type { CitationHighlight } from 'models/CitationHighlight';

import type { CitationViewerTarget } from './CitationDocumentViewer';

// Lazy for the same reason as the citation badge's preview: react-pdf pulls in pdf.js, which needs
// browser globals (DOMMatrix) and weighs a lot. Nothing loads until a citation is actually opened.
const CitationDocumentViewer = lazy(() => import('./CitationDocumentViewer'));

// The foldouts' slide duration: the open document is forgotten only once the drawer has finished
// sliding shut, so the two leave together instead of the drawer widening on its way out.
const CLOSE_ANIMATION_MS = 500;

/**
 * The citation viewer, for a panel whose citations open in it: `openViewer` shows a document at its
 * cited evidence, and `viewer` is the element to render (null until first opened). `parentOpen` is
 * the hosting drawer's open state, so the viewer slides out alongside it rather than vanishing when
 * the drawer unmounts its content.
 *
 * `backdrop` is for a viewer opened straight from the page. Opened from a drawer, the drawer's own
 * backdrop already dims the page behind both, and a second one would stack on top of it.
 */
export function useCitationViewer({
  attachmentType,
  parentOpen,
  backdrop = false,
}: {
  attachmentType: string;
  parentOpen: boolean;
  backdrop?: boolean;
}): {
  openViewer: (target: CitationViewerTarget) => void;
  viewer: ReactNode;
} {
  const [target, setTarget] = useState<CitationViewerTarget | null>(null);
  const [used, setUsed] = useState(false);

  const openViewer = useCallback((next: CitationViewerTarget) => {
    setUsed(true);
    setTarget(next);
  }, []);

  // Closing the drawer forgets the open document. A drawer that stays mounted while shut would
  // otherwise reopen straight onto it; the viewer should appear only when a citation is clicked.
  useEffect(() => {
    if (parentOpen) return;
    const timer = setTimeout(() => setTarget(null), CLOSE_ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [parentOpen]);

  const close = useCallback(() => setTarget(null), []);

  const viewer = used ? (
    <Suspense fallback={null}>
      <CitationDocumentViewer
        target={target}
        open={parentOpen}
        attachmentType={attachmentType}
        backdrop={backdrop}
        onClose={close}
      />
    </Suspense>
  ) : null;

  return { openViewer, viewer };
}

// Only a PDF can be drawn and outlined; anything else keeps opening in a new tab.
export function isViewablePdf(filename: string | null | undefined): boolean {
  return /\.pdf$/i.test(filename ?? '');
}

export type CitedSource = {
  attachmentId: string;
  displayId: number;
  pages?: number[];
  highlights?: CitationHighlight[];
};

// The attachable a referral's own documents hang off, for opening one of them.
export const WORKFLOW_ATTACHABLE_MODEL = 'Workflow';

/**
 * How a cited source is labelled and opened, the one rule every citing panel follows. `filename` is
 * the attachment's own name joined off the workflow, or nothing when the run no longer holds it; the
 * label then falls back to the display id rather than a blank. `target` is what the viewer opens, or
 * null when the file is not a PDF and has to open in a new tab instead.
 */
export function citationSource(
  source: CitedSource,
  filename: string | null | undefined
): { label: string; target: CitationViewerTarget | null } {
  const label = filename || `Document ${source.displayId}`;
  return {
    label,
    target: isViewablePdf(filename)
      ? { attachmentId: source.attachmentId, filename: label, pages: source.pages, highlights: source.highlights }
      : null,
  };
}
